import { useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from '../lib/auth'
import Wordmark from '../components/Wordmark'

type Mode = 'signIn' | 'signUp'

/**
 * Sign in / create account.
 *
 * The native version fills its primary button with pure white in both skins,
 * which on Daylight is a white slab on a near-white page and on Showtime
 * competes with the mode switcher for the loudest thing on screen. Here the
 * primary action is the accent, which is what "one accent, used functionally"
 * is for — the single most important control on the screen is the single
 * orange thing on it.
 */
export default function SignIn() {
  const {
    user,
    needsEmailVerification,
    signIn,
    signUp,
    signInWithGoogle,
    resendVerification,
    resetPassword,
    reloadUser,
    signOut,
  } = useAuth()

  const [mode, setMode] = useState<Mode>('signIn')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const canSubmit =
    !busy &&
    email.trim().length > 0 &&
    password.length >= 6 &&
    (mode === 'signIn' || name.trim().length > 0)

  async function run(action: () => Promise<void>) {
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await action()
    } catch (caught) {
      setError(readableAuthError(caught))
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!canSubmit) return
    void run(async () => {
      if (mode === 'signIn') await signIn(email, password)
      else await signUp(email, password, name)
    })
  }

  // Signed in but unverified. Holding here rather than dropping them into the
  // app means the link can't be skipped by simply signing back in.
  if (user && needsEmailVerification) {
    return (
      <AuthCanvas>
        <div className="stack" style={{ gap: 18 }}>
          <Wordmark />
          <div className="stack" style={{ gap: 6 }}>
            <h1 className="t-display-m">Check your email</h1>
            <p className="t-body dim">
              We sent a verification link to <strong>{user.email}</strong>. Open it, then come
              back and continue.
            </p>
          </div>

          {error && <Banner tone="error">{error}</Banner>}
          {info && <Banner tone="info">{info}</Banner>}

          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const verified = await reloadUser()
                if (!verified) {
                  setInfo("Not verified yet — open the link in your inbox, then try again.")
                }
              })
            }
          >
            {busy ? 'Checking…' : "I've verified my email"}
          </button>

          <div className="row" style={{ justifyContent: 'space-between' }}>
            <button
              className="btn btn-quiet"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await resendVerification()
                  setInfo('Sent. Check your inbox and spam folder.')
                })
              }
            >
              Resend email
            </button>
            <button className="btn btn-quiet" onClick={() => void signOut()}>
              Use a different account
            </button>
          </div>
        </div>
      </AuthCanvas>
    )
  }

  return (
    <AuthCanvas>
      <form className="stack" style={{ gap: 18 }} onSubmit={onSubmit}>
        <div className="stack" style={{ gap: 12 }}>
          <Wordmark />
          <p className="t-body dim">
            {mode === 'signIn'
              ? 'Every date, flight and lobby call in one place.'
              : 'Set up your first tour in a couple of minutes.'}
          </p>
        </div>

        <ModeSwitcher mode={mode} onChange={setMode} />

        <div className="stack" style={{ gap: 10 }}>
          {mode === 'signUp' && (
            <div>
              <label className="label" htmlFor="name">
                Full name
              </label>
              <input
                id="name"
                className="field"
                autoComplete="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
          )}

          <div>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="field"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="field"
              type="password"
              autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {mode === 'signUp' && password.length > 0 && password.length < 6 && (
              <p className="t-caption" style={{ color: 'var(--warning)', marginTop: 6 }}>
                Passwords need at least 6 characters.
              </p>
            )}
          </div>
        </div>

        {error && <Banner tone="error">{error}</Banner>}
        {info && <Banner tone="info">{info}</Banner>}

        <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
          {busy ? 'Working…' : mode === 'signIn' ? 'Sign In' : 'Create Account'}
        </button>

        <div className="row" aria-hidden>
          <hr className="hairline grow" />
          <span className="t-eyebrow">or</span>
          <hr className="hairline grow" />
        </div>

        {/* Google's brand terms require their mark on their own light surface,
            so this one control stays white in both skins by design. */}
        <button
          type="button"
          className="btn"
          disabled={busy}
          style={{ background: '#fff', color: 'rgb(0 0 0 / 0.82)' }}
          onClick={() => void run(signInWithGoogle)}
        >
          <GoogleMark />
          Continue with Google
        </button>

        {mode === 'signIn' && (
          <button
            type="button"
            className="btn btn-quiet"
            style={{ alignSelf: 'center' }}
            disabled={busy || email.trim().length === 0}
            onClick={() =>
              void run(async () => {
                await resetPassword(email)
                setInfo('Password reset sent. Check your inbox.')
              })
            }
          >
            Forgot your password?
          </button>
        )}
      </form>
    </AuthCanvas>
  )
}

/* ------------------------------------------------------------------ pieces */

function AuthCanvas({ children }: { children: React.ReactNode }) {
  return (
    // Sign-in is always Daylight. It is the first screen anyone sees, it is
    // reached straight off a light marketing site, and a machine set to dark
    // shouldn't decide that the front door of the product is black. Scoped to
    // this subtree, so the app's own skin preference is untouched.
    <div
      data-skin-scope="daylight"
      style={{
        position: 'relative',
        minHeight: '100%',
        background: 'var(--bg)',
        color: 'var(--text-primary)',
      }}
    >
      {/* One slow accent wash bled off the top corner. The native version runs
          this at 0.22 across a 620pt radius, which reads as a brown smudge
          over half the screen rather than a signal; kept tight and faint here. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(60vw 44vh at 8% -6%, var(--accent-wash), transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* The way back out. Someone who followed "Open Encore" from the site and
          isn't ready to sign up needs a door that isn't the browser's back
          button. */}
      <a
        href="/"
        className="t-caption"
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderRadius: 999,
          border: '1px solid var(--hairline)',
          background: 'var(--panel)',
          color: 'var(--text-primary)',
          textDecoration: 'none',
        }}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
          <path
            d="M15 5 8 12l7 7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back to encore-app.com
      </a>

      <div
        style={{
          position: 'relative',
          minHeight: '100%',
          display: 'grid',
          placeItems: 'center',
          padding: '84px 24px 48px',
        }}
      >
        <div className="panel panel-pad" style={{ width: '100%', maxWidth: 400, padding: 26 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

function ModeSwitcher({ mode, onChange }: { mode: Mode; onChange: (next: Mode) => void }) {
  const options: Array<{ value: Mode; label: string }> = [
    { value: 'signIn', label: 'Sign In' },
    { value: 'signUp', label: 'Create Account' },
  ]

  return (
    <div
      role="tablist"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 4,
        padding: 4,
        background: 'var(--panel-raised)',
        borderRadius: 'var(--radius-chip)',
      }}
    >
      {options.map((option) => {
        const selected = mode === option.value
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={selected}
            onClick={() => onChange(option.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-chip)',
              fontSize: '0.85rem',
              fontWeight: 600,
              // The selected tab is a quiet raised surface, not a white slab.
              // Orange is spent on the primary action instead — there is only
              // one of it per screen.
              background: selected ? 'var(--panel)' : 'transparent',
              color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: selected ? 'var(--shadow-card)' : 'none',
            }}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function Banner({ tone, children }: { tone: 'error' | 'info'; children: React.ReactNode }) {
  const color = tone === 'error' ? 'var(--destructive)' : 'var(--accent-text)'
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className="t-caption"
      style={{
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        padding: '9px 12px',
        borderRadius: 'var(--radius-control)',
      }}
    >
      {children}
    </p>
  )
}

function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#4285F4"
        d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.3h12.1c-.2 2-1.6 5-4.5 7l6.9 5.4c4.1-3.8 6.6-9.4 6.6-15.7z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.9-5.4c-1.9 1.3-4.4 2.2-7.6 2.2-5.8 0-10.7-3.8-12.5-9.1l-7.1 5.5C8.1 41.1 15.5 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.5 28.4c-.5-1.4-.7-2.9-.7-4.4s.3-3 .7-4.4l-7.1-5.5C2.9 17 2 20.4 2 24s.9 7 2.4 9.9l7.1-5.5z"
      />
      <path
        fill="#EA4335"
        d="M24 10.5c3.2 0 6 1.1 8.2 3.2l6.1-6.1C34.9 4.1 29.9 2 24 2 15.5 2 8.1 6.9 4.4 14.1l7.1 5.5c1.8-5.3 6.7-9.1 12.5-9.1z"
      />
    </svg>
  )
}

/** Firebase error codes are not for humans. */
function readableAuthError(caught: unknown): string {
  const code = (caught as { code?: string })?.code ?? ''
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return "That email and password don't match an account."
    case 'auth/email-already-in-use':
      return 'There is already an account with that email. Try signing in.'
    case 'auth/invalid-email':
      return "That doesn't look like an email address."
    case 'auth/weak-password':
      return 'Passwords need at least 6 characters.'
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a moment and try again.'
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Google sign-in was closed before it finished.'
    case 'auth/network-request-failed':
      return "Couldn't reach Encore. Check your connection."
    default:
      return (caught as Error)?.message ?? 'Something went wrong.'
  }
}
